/**
 * SEO 中枢配置。
 *
 * 这一份数据同时被三处消费，改这里就够了：
 *   1. 各页面的 metaInfo（vue-meta）—— title / description / canonical / og / hreflang
 *   2. scripts/prerender.js —— 决定预渲染哪些路由
 *   3. scripts/gen-sitemap.js —— 生成 sitemap.xml
 *
 * canonical 统一指向 SITE_ORIGIN。站点同时挂在 www.psmirror.cn、psmirror.cn、
 * www.designmirror.cn 三个域名上，内容完全相同；CDN 当前档位不支持 rewrite 做不了
 * 301，靠 canonical 告诉搜索引擎只认主域名这一份。
 */

// 主域名。三个域名的所有页面都声明 canonical 指向这里。
const SITE_ORIGIN = 'https://www.designmirror.cn'

const DEFAULT_OG_IMAGE = '/images/og-cover.jpg'

const LOCALES = ['zh', 'en']

// og:locale 用的完整语言标记
const OG_LOCALE = { zh: 'zh_CN', en: 'en_US' }

// hreflang 用的语言标记
const HREFLANG = { zh: 'zh-Hans', en: 'en' }

/**
 * pages 顺序即 sitemap 顺序。
 *
 * index=false 的页面依赖登录态或属于交易流程，收录了只会稀释权重，
 * 这些页面不预渲染、加 noindex、不进 sitemap。
 *
 * priority / changefreq 仅用于 sitemap。
 */
const pages = [
  {
    path: '/',
    index: true,
    priority: '1.0',
    changefreq: 'weekly',
    zh: {
      title: 'Design Mirror - 设计稿实时预览，手机秒看 PS / Figma / Sketch 设计图',
      description:
        '设计稿实时预览工具，在手机、平板上实时查看 Photoshop、Illustrator、Sketch、XD、Figma、MasterGo、即时设计中的设计图。支持 Wi-Fi 与 USB 连接，多设备同步预览。'
    },
    en: {
      title: 'Design Mirror - Preview Your Design Live on Any Device',
      description:
        'Preview designs from Photoshop, Illustrator, Sketch, XD, Figma, MasterGo and JSDesign live on your phone or tablet. Wi-Fi and USB connection, multi-device sync.'
    }
  },
  {
    path: '/v4',
    index: true,
    priority: '0.9',
    changefreq: 'weekly',
    zh: {
      title: 'Design Mirror V4 全新发布 - 连接更稳，支持 USB 免 Wi-Fi',
      description:
        'V4 用全新架构重写，主攻「连不上」问题：连接稳定性大幅提升，新增 USB 免 Wi-Fi 连接，全新原生 App，一个安装包覆盖多个设计工具。'
    },
    en: {
      title: 'Design Mirror V4 - Rebuilt for Rock-Solid Connections',
      description:
        'V4 is a full rewrite focused on connection reliability: far more stable links, new USB (no Wi-Fi) mode, a new native app, and one installer for every design tool.'
    }
  },
  {
    path: '/photoshop',
    index: true,
    priority: '0.9',
    changefreq: 'monthly',
    zh: {
      title: 'Photoshop 设计稿手机实时预览 - PS 插件安装教程 | Design Mirror',
      description:
        'Design Mirror 的 Photoshop 插件安装教程。装好后可在 PS 中直接把设计稿实时投到手机、平板上查看，面板内可见预览图与设备连接状态。支持 CC2018 及以上版本。'
    },
    en: {
      title: 'Photoshop Live Preview on Mobile - Plugin Setup | Design Mirror',
      description:
        'How to install the Design Mirror plugin for Photoshop. Mirror your PS canvas to phones and tablets in real time, with preview and device status right inside the panel. Supports CC2018 and later.'
    }
  },
  {
    path: '/sketch',
    index: true,
    priority: '0.9',
    changefreq: 'monthly',
    zh: {
      title: 'Sketch 设计稿手机实时预览 - Sketch 插件安装教程 | Design Mirror',
      description:
        'Design Mirror 的 Sketch 插件安装教程，可替代官方 Sketch Mirror。在 Sketch 里直接把画板实时预览到手机上，无需额外预览 App。支持 Sketch 70 及以上版本。'
    },
    en: {
      title: 'Sketch Live Preview on Mobile - Plugin Setup | Design Mirror',
      description:
        'Install the Design Mirror plugin for Sketch as an alternative to Sketch Mirror. Preview artboards on your phone straight from Sketch, no extra app needed. Supports Sketch 70+.'
    }
  },
  {
    path: '/xd',
    index: true,
    priority: '0.8',
    changefreq: 'monthly',
    zh: {
      title: 'Adobe XD 设计稿手机实时预览 - XD 插件安装教程 | Design Mirror',
      description:
        'Design Mirror 的 Adobe XD 插件安装教程。安装后可在 XD 中把设计稿实时同步到手机、平板查看，支持多设备同时预览。'
    },
    en: {
      title: 'Adobe XD Live Preview on Mobile - Plugin Setup | Design Mirror',
      description:
        'Install the Design Mirror plugin for Adobe XD and mirror your designs to phones and tablets in real time, with multi-device preview support.'
    }
  },
  {
    path: '/illustrator',
    index: true,
    priority: '0.8',
    changefreq: 'monthly',
    zh: {
      title: 'Illustrator 设计稿手机实时预览 - AI 插件安装教程 | Design Mirror',
      description:
        'Design Mirror 4.0 新增支持 Adobe Illustrator。安装插件后即可在 AI 中把画板实时预览到手机上，随时确认真机效果。'
    },
    en: {
      title: 'Illustrator Live Preview on Mobile - Plugin Setup | Design Mirror',
      description:
        'Design Mirror 4.0 adds Adobe Illustrator support. Install the plugin and mirror your AI artboards to a phone in real time to check them on a real screen.'
    }
  },
  {
    path: '/figma',
    index: true,
    priority: '0.9',
    changefreq: 'monthly',
    zh: {
      title: 'Figma 设计稿手机实时预览 - Figma 插件安装教程 | Design Mirror',
      description:
        'Design Mirror 的 Figma 插件安装教程。导入插件包后即可在 Figma 中把设计稿实时投到手机、平板上查看，支持多设备同步。'
    },
    en: {
      title: 'Figma Live Preview on Mobile - Plugin Setup | Design Mirror',
      description:
        'Install the Design Mirror plugin for Figma. Import the plugin package and mirror your Figma frames to phones and tablets in real time, with multi-device sync.'
    }
  },
  {
    path: '/jsdesign',
    index: true,
    priority: '0.8',
    changefreq: 'monthly',
    zh: {
      title: '即时设计手机实时预览 - 即时设计插件安装教程 | Design Mirror',
      description:
        'Design Mirror 支持国产设计工具「即时设计」。导入插件包后即可把设计稿实时预览到手机上，随时查看真机效果。'
    },
    en: {
      title: 'JSDesign Live Preview on Mobile - Plugin Setup | Design Mirror',
      description:
        'Design Mirror supports JSDesign. Import the plugin package to mirror your designs to a phone in real time and check them on a real device.'
    }
  },
  {
    path: '/mastergo',
    index: true,
    priority: '0.8',
    changefreq: 'monthly',
    zh: {
      title: 'MasterGo 手机实时预览 - MasterGo 插件安装教程 | Design Mirror',
      description:
        'Design Mirror 的 MasterGo 插件安装教程。导入插件包后即可在 MasterGo 中把设计稿实时投到手机上查看。'
    },
    en: {
      title: 'MasterGo Live Preview on Mobile - Plugin Setup | Design Mirror',
      description:
        'Install the Design Mirror plugin for MasterGo and mirror your designs to a phone in real time.'
    }
  },
  {
    path: '/mobile',
    index: true,
    priority: '0.8',
    changefreq: 'monthly',
    zh: {
      title: 'Design Mirror App 下载 - iOS / Android 设计稿预览客户端',
      description:
        'Design Mirror 手机 App 下载，支持 iOS 与 Android。配合桌面端使用，可实时接收设计稿，支持缩放适配、多画布切换、截屏快照与图像对比。'
    },
    en: {
      title: 'Download the Design Mirror App - iOS & Android',
      description:
        'Download the Design Mirror mobile app for iOS and Android. Pair it with the desktop app to receive designs live, with zoom-to-fit, canvas switching, snapshots and image comparison.'
    }
  },
  {
    path: '/help',
    index: true,
    priority: '0.7',
    changefreq: 'monthly',
    zh: {
      title: '帮助中心与常见问题 - 安装、连接、账号问题解答 | Design Mirror',
      description:
        'Design Mirror 常见问题解答：各设计软件的插件安装方法、手机连不上电脑怎么办、账号与订阅相关问题，以及联系开发者的方式。'
    },
    en: {
      title: 'Help Center & FAQ - Setup, Connection, Account | Design Mirror',
      description:
        'Design Mirror FAQ: how to install the plugin for each design tool, what to do when your phone will not connect, account and subscription questions, and how to reach the developer.'
    }
  },
  {
    path: '/buy',
    index: true,
    priority: '0.8',
    changefreq: 'monthly',
    zh: {
      title: '价格与购买 - 15 天免费试用 | Design Mirror',
      description:
        'Design Mirror 订阅方案与价格。新用户可享 15 天免费试用，试用期内可体验完整功能，满意后再选择合适的订阅时长付费。'
    },
    en: {
      title: 'Pricing & Plans - 15-Day Free Trial | Design Mirror',
      description:
        'Design Mirror subscription plans and pricing. New users get a 15-day free trial with full features, so you can decide before you pay.'
    }
  },
  {
    path: '/team',
    index: true,
    priority: '0.7',
    changefreq: 'monthly',
    zh: {
      title: '团队版与企业采购 - 多席位统一管理 | Design Mirror',
      description:
        'Design Mirror 团队版，支持多席位统一购买与成员管理，适合设计团队和企业批量采购。'
    },
    en: {
      title: 'Team & Enterprise Plans - Manage Seats in One Place | Design Mirror',
      description:
        'Design Mirror for teams: buy and manage multiple seats in one place, built for design teams and company-wide rollouts.'
    }
  },
  {
    path: '/terms',
    index: true,
    priority: '0.3',
    changefreq: 'yearly',
    zh: {
      title: '服务协议 | Design Mirror',
      description: 'Design Mirror 服务协议与用户条款。'
    },
    en: {
      title: 'Terms of Service | Design Mirror',
      description: 'Design Mirror terms of service and user agreement.'
    }
  },
  {
    path: '/privacy',
    index: true,
    priority: '0.3',
    changefreq: 'yearly',
    zh: {
      title: '隐私政策 | Design Mirror',
      description: 'Design Mirror 隐私政策，说明我们收集哪些信息、如何使用与保护这些信息。'
    },
    en: {
      title: 'Privacy Policy | Design Mirror',
      description:
        'Design Mirror privacy policy: what we collect, how we use it, and how we protect it.'
    }
  },

  // ---- 以下不索引：依赖登录态或属于交易流程 ----
  { path: '/auth-login', index: false, zh: { title: '登录' }, en: { title: 'Sign In' } },
  { path: '/auth-signup', index: false, zh: { title: '注册' }, en: { title: 'Sign Up' } },
  {
    path: '/auth-re-password',
    index: false,
    zh: { title: '重置密码' },
    en: { title: 'Reset Password' }
  },
  { path: '/profile', index: false, zh: { title: '个人中心' }, en: { title: 'My Account' } },
  { path: '/shop-cart', index: false, zh: { title: '订单确认' }, en: { title: 'Checkout' } },
  { path: '/shop-result', index: false, zh: { title: '支付结果' }, en: { title: 'Payment Result' } },
  {
    path: '/member-update',
    index: false,
    zh: { title: '成员管理' },
    en: { title: 'Manage Members' }
  },
  // 目前无内容，先不索引；填充文章后把 index 改为 true 即可进 sitemap
  { path: '/blog-list', index: false, zh: { title: '博客' }, en: { title: 'Blog' } }
]

/** 需要预渲染 + 进 sitemap 的页面 */
const indexablePages = pages.filter(p => p.index)

/** 把路由 path 与语言拼成实际 URL 路径。中文在根，英文在 /en 下。 */
function localizedPath(path, locale) {
  if (locale === 'zh') return path
  return path === '/' ? '/en' : `/en${path}`
}

/** 完整 canonical URL，始终指向主域名 */
function canonicalUrl(path, locale) {
  return SITE_ORIGIN + localizedPath(path, locale)
}

function findPage(path) {
  return pages.find(p => p.path === path)
}

/**
 * 从实际路由路径反解出「基础路径 + 语言」。
 * /en/photoshop -> { path: '/photoshop', locale: 'en' }
 * /photoshop    -> { path: '/photoshop', locale: 'zh' }
 * /en           -> { path: '/',          locale: 'en' }
 */
function stripLocale(routePath) {
  const p = routePath.replace(/\/+$/, '') || '/'
  if (p === '/en') return { path: '/', locale: 'en' }
  if (p.startsWith('/en/')) return { path: p.slice(3), locale: 'en' }
  return { path: p, locale: 'zh' }
}

// CommonJS 导出：这样前端 webpack 可以具名 import，
// scripts/ 下的 Node 脚本也能直接 require，避免路由表出现两份。
module.exports = {
  SITE_ORIGIN,
  DEFAULT_OG_IMAGE,
  LOCALES,
  OG_LOCALE,
  HREFLANG,
  pages,
  indexablePages,
  localizedPath,
  canonicalUrl,
  findPage,
  stripLocale,
}
